import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouterHooks } from "../../../withRouters/withRoutersHook";
import { UploadPDFTemplateAction, SetActivePDFTemplateAction } from "../../../Actions/Forms";
import { X, Upload, FileText, Loader2, AlertCircle } from "lucide-react";

class PDFTemplateUpload extends Component {
  static propTypes = {
    UploadPDFTemplateAction: PropTypes.func.isRequired,
    SetActivePDFTemplateAction: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
  };

  static defaultProps = {
    isOpen: false,
    onClose: () => {},
  };

  state = {
    name: "",
    pdfFile: null,
    dragOver: false,
    uploading: false,
    error: null,
  };

  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      this.setState({ pdfFile: file, error: null });
    } else {
      this.setState({ error: "Please select a valid PDF file." });
    }
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ dragOver: false });
    const file = e.dataTransfer.files[0];
    if (file && file.type === "application/pdf") {
      this.setState({ pdfFile: file, error: null });
    } else {
      this.setState({ error: "Please drop a valid PDF file." });
    }
  };

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ dragOver: true });
  };

  handleDragLeave = () => this.setState({ dragOver: false });

  handleClose = () => {
    if (!this.state.uploading) {
      this.setState({ name: "", pdfFile: null, dragOver: false, error: null });
      this.props.onClose();
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { name, pdfFile } = this.state;
    if (!name.trim()) {
      this.setState({ error: "Please enter a template name." });
      return;
    }
    if (!pdfFile) {
      this.setState({ error: "Please select a PDF file." });
      return;
    }

    this.setState({ uploading: true, error: null });
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("pdf_file", pdfFile);

    try {
      const data = await this.props.UploadPDFTemplateAction(formData);
      this.props.SetActivePDFTemplateAction(data);
      this.props.onClose();
      this.props.navigate(`/dashboard/smartflow/${data.id}/map`);
    } catch {
      this.setState({ uploading: false, error: "Upload failed. Please try again." });
    }
  };

  render() {
    const { isOpen } = this.props;
    const { name, pdfFile, dragOver, uploading, error } = this.state;

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) this.handleClose(); }}
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="font-SFPro font-bold text-xl text-gray-900">New PDF Template</h1>
              <p className="font-inter text-sm text-gray-400 mt-0.5">
                Upload a PDF to start mapping fields visually.
              </p>
            </div>
            <button
              onClick={this.handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={this.handleSubmit} className="flex flex-col gap-5">
            {/* Name field */}
            <div className="flex flex-col gap-1.5">
              <label className="font-inter font-medium text-sm text-gray-700">Template name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => this.setState({ name: e.target.value })}
                placeholder="e.g. Customer Onboarding Form"
                className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:border-blue-500 transition-all"
              />
            </div>

            {/* PDF Drop Zone */}
            <div className="flex flex-col gap-1.5">
              <label className="font-inter font-medium text-sm text-gray-700">PDF file</label>
              <div
                onDrop={this.handleDrop}
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                className={`relative flex flex-col items-center justify-center gap-3 h-40 border-2 border-dashed rounded-xl transition-all cursor-pointer ${
                  dragOver
                    ? "border-blue-400 bg-blue-50"
                    : pdfFile
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() => document.getElementById("pdf-file-input").click()}
              >
                <input
                  id="pdf-file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={this.handleFileChange}
                  className="hidden"
                />
                {pdfFile ? (
                  <>
                    <FileText className="w-8 h-8 text-emerald-500" />
                    <p className="font-inter font-medium text-sm text-emerald-700">{pdfFile.name}</p>
                    <p className="font-inter text-xs text-emerald-500">
                      {(pdfFile.size / 1024 / 1024).toFixed(2)} MB · Click to change
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-300" />
                    <p className="font-inter text-sm text-gray-400">
                      Drag & drop a PDF here, or <span className="text-blue-500 font-medium">browse</span>
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm font-inter text-red-600">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={uploading || !name || !pdfFile}
              className="flex items-center justify-center gap-2 w-full h-11 bg-blue-600 text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
              ) : (
                <>Upload & Map Fields</>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }
}

export default withRouterHooks(
  connect(null, { UploadPDFTemplateAction, SetActivePDFTemplateAction })(PDFTemplateUpload)
);
